package music.jammers.client.shared.event.track.presentation.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.create_event_track_screen_add_button
import limasoul.client.shared.generated.resources.create_event_track_screen_additional
import limasoul.client.shared.generated.resources.create_event_track_screen_artist
import limasoul.client.shared.generated.resources.create_event_track_screen_comment
import limasoul.client.shared.generated.resources.create_event_track_screen_members
import limasoul.client.shared.generated.resources.create_event_track_screen_select_track
import limasoul.client.shared.generated.resources.create_event_track_screen_title
import limasoul.client.shared.generated.resources.create_event_track_screen_track
import music.jammers.client.shared.component.AppTextField
import music.jammers.client.shared.component.BackgroundBox
import music.jammers.client.shared.component.ErrorDialog
import music.jammers.client.shared.component.FieldValidationState
import music.jammers.client.shared.component.JammersTopAppBar
import music.jammers.client.shared.component.Progress
import music.jammers.client.shared.component.member.info.legend.presentation.ui.MemberInfoLegendComponent
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase
import music.jammers.client.shared.event.presentation.ui.getString
import music.jammers.client.shared.event.track.di.EventTrackComponent
import music.jammers.client.shared.event.track.domain.usecase.ValidateEventTrackUseCase
import music.jammers.client.shared.event.track.presentation.viewmodel.AddEventTrackState
import music.jammers.client.shared.event.track.presentation.viewmodel.AddEventTrackViewModel
import org.jetbrains.compose.resources.stringResource

const val ADD_EVENT_TRACK_SCREEN = "AddEventTrackScreen"

@Composable
fun AddEventTrackScreen(eventId: Long) {
    val viewModel = remember { EventTrackComponent.addEventTrackViewModel.apply { onReady(eventId) } }
    val state = viewModel.state.value
    val validationState = viewModel.validationState.value

    Scaffold(
        topBar = {
            JammersTopAppBar(
                text = stringResource(Res.string.create_event_track_screen_title),
                navigationIcon = {
                    IconButton(onClick = { viewModel.onBackPressed() }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                },
            )
        },
    ) { paddingValues: PaddingValues ->
        BackgroundBox {
            if (state.progress) {
                Progress()
            } else {
                Column(
                    modifier = Modifier.padding(paddingValues).verticalScroll(rememberScrollState())
                ) {
                    Content(state, validationState, viewModel)
                }
            }
        }
    }
}

@Composable
private fun Content(
    state: AddEventTrackState,
    validationState: FieldValidationState,
    viewModel: AddEventTrackViewModel,
) {
    state.error?.let { error ->
        ErrorDialog(error.message ?: "error") {
            viewModel.onErrorShown()
        }
    }

    Column(modifier = Modifier.padding(horizontal = 32.dp)) {
        SelectTrackContent(state, validationState, viewModel)
        AdditionalContent(state, viewModel)
        MembersContent(state, validationState, viewModel)

        OutlinedButton(
            onClick = {
                viewModel.onAddTrackClicked()
            },
            enabled = state.addButtonEnabled,
            modifier = Modifier
                .align(Alignment.CenterHorizontally)
                .padding(bottom = 16.dp),
        ) {
            Text(
                text = stringResource(Res.string.create_event_track_screen_add_button),
                color = MaterialTheme.colorScheme.primary,
                style = MaterialTheme.typography.headlineSmall,
                modifier = Modifier.padding(horizontal = 64.dp),
            )
        }
    }
}

@Composable
private fun SelectTrackContent(
    state: AddEventTrackState,
    validationState: FieldValidationState,
    viewModel: AddEventTrackViewModel,
) {
    Text(
        text = stringResource(Res.string.create_event_track_screen_select_track).uppercase(),
        style = MaterialTheme.typography.headlineSmall,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth()
            .padding(8.dp),
    )

    val artistError = validationState.getValidationError(ValidateEventTrackUseCase.ARTIST_NAME).getString()

    AppTextField(
        value = state.artistName,
        placeholder = { Text(stringResource(Res.string.create_event_track_screen_artist)) },
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Sentences,
            autoCorrectEnabled = true,
            keyboardType = KeyboardType.Text,
            imeAction = ImeAction.Next
        ),
        onValueChange = { value ->
            viewModel.onArtistChanged(value)
        },
        errorText = artistError,
        modifier = Modifier.fillMaxWidth(),
    )

    val trackError = validationState.getValidationError(ValidateEventTrackUseCase.TRACK_NAME).getString()
    AppTextField(
        value = state.trackTitle,
        placeholder = { Text(stringResource(Res.string.create_event_track_screen_track)) },
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Sentences,
            autoCorrectEnabled = true,
            keyboardType = KeyboardType.Text,
            imeAction = ImeAction.Next
        ),
        onValueChange = { value ->
            viewModel.onTrackChanged(value)
        },
        errorText = trackError,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun AdditionalContent(
    state: AddEventTrackState,
    viewModel: AddEventTrackViewModel,
) {
    Text(
        text = stringResource(Res.string.create_event_track_screen_additional).uppercase(),
        style = MaterialTheme.typography.headlineSmall,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth()
            .padding(8.dp),
    )

    AppTextField(
        value = state.comment,
        placeholder = { Text(stringResource(Res.string.create_event_track_screen_comment)) },
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Sentences,
            autoCorrectEnabled = true,
            keyboardType = KeyboardType.Text,
            imeAction = ImeAction.Next
        ),
        onValueChange = { value ->
            viewModel.onCommentChanged(value)
        },
        modifier = Modifier.fillMaxWidth(),
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun MembersContent(
    state: AddEventTrackState,
    validationState: FieldValidationState,
    viewModel: AddEventTrackViewModel,
) {
    Row(
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = stringResource(Res.string.create_event_track_screen_members),
            style = MaterialTheme.typography.headlineMedium,
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(vertical = 8.dp),
        )

        MemberInfoLegendComponent(extended = true)
    }

    FlowRow(modifier = Modifier) {
        state.selectedInstruments.forEach { item ->
            UpdateInstrumentImage(
                updateInstrument = item,
                onClick = {
                    viewModel.onInstrumentClicked(item)
                }
            )

            Spacer(Modifier.size(8.dp))
        }

        Spacer(Modifier.size(8.dp))
    }

    val selectedInstrumentError = validationState.getValidationError(ValidateEventUseCase.SELECTED_INSTRUMENTS)
        .getString()

    if (selectedInstrumentError == null) {
        Spacer(modifier = Modifier.size(16.dp))
    } else {
        Text(
            text = selectedInstrumentError,
            style = MaterialTheme.typography.labelMedium,
            maxLines = 1,
        )
    }

    Spacer(modifier = Modifier.size(16.dp))
}